import { useState, useCallback, useMemo } from 'react';
import { convertToDateRangeFilter } from '../utils/helpers';

export function useFilterManagement(initialFilters = {}) {
  const [filters, setFilters] = useState(initialFilters);
  const [pendingFilters, setPendingFilters] = useState(initialFilters);
  const [showFilterFor, setShowFilterFor] = useState(null);
  const [filterGroups, setFilterGroups] = useState({});
  const [maxValues, setMaxValues] = useState({});

  // Handle filter changes (only updates pending filters, doesn't trigger API call)
  const handleFilterChange = useCallback((columnKey, filterType, value) => {
    setPendingFilters(prev => {
      const newFilters = { ...prev };
      if (!newFilters[columnKey]) {
        newFilters[columnKey] = {};
      }
      
      // For 'contains' and 'equals' filter types, allow empty string to preserve mode selection
      // For other filter types, delete on empty/null/undefined as before
      if (value === null || value === undefined || (value === '' && filterType !== 'contains' && filterType !== 'equals')) {
        delete newFilters[columnKey][filterType];
        if (Object.keys(newFilters[columnKey]).length === 0) {
          delete newFilters[columnKey];
        }
      } else {
        newFilters[columnKey][filterType] = value;
      }
      
      return newFilters;
    });
  }, []);

  // Helper function to detect which operator is used in a filter
  const getFilterOperator = useCallback((filter) => {
    if (!filter || typeof filter !== 'object') return 'in';
    if ('in' in filter) return 'in';
    if ('and' in filter) return 'and';
    if ('not' in filter) return 'not';
    if ('equals' in filter) return 'equals';
    return 'in'; // Default to 'in' for backward compatibility
  }, []);

  // Handle multiple selection for dropdown fields
  const handleMultiSelectChange = useCallback((columnKey, selectedOptions, operator = 'in') => {
    setPendingFilters(prev => {
      const newFilters = { ...prev };
      
      if (!selectedOptions || selectedOptions.length === 0) {
        delete newFilters[columnKey];
      } else {
        // Use the specified operator (in, and, or not)
        // Clear any existing operator keys first
        const cleanFilter = {};
        cleanFilter[operator] = selectedOptions;
        newFilters[columnKey] = cleanFilter;
      }
      
      return newFilters;
    });
  }, []);

  // Handle range slider changes for number fields
  const handleRangeChange = useCallback((columnKey, range, getNumberFieldConfigWithServerData) => {
    setPendingFilters(prev => {
      const newFilters = { ...prev };
      if (!newFilters[columnKey]) {
        newFilters[columnKey] = {};
      }
      
      const config = getNumberFieldConfigWithServerData(columnKey);
      const [minVal, maxVal] = range;
      
      // Only set min if it's different from the field's minimum
      if (minVal > config.min) {
        newFilters[columnKey].min = minVal;
      } else {
        delete newFilters[columnKey].min;
      }
      
      // Only set max if it's different from the field's maximum
      if (maxVal < config.max) {
        newFilters[columnKey].max = maxVal;
      } else {
        delete newFilters[columnKey].max;
      }
      
      // Clean up empty filter objects
      if (Object.keys(newFilters[columnKey]).length === 0) {
        delete newFilters[columnKey];
      }
      
      return newFilters;
    });
  }, []);

  // Apply pending filters
  const applyFilters = useCallback((setCurrentPage) => {
    const newFilters = { ...pendingFilters };
    setFilters(newFilters);
    if (setCurrentPage) setCurrentPage(1);
    setShowFilterFor(null);
  }, [pendingFilters]);

  // Clear a specific filter
  const clearFilter = useCallback((columnKey, setCurrentPage) => {
    const newFilters = { ...filters };
    delete newFilters[columnKey];
    setPendingFilters(newFilters);
    setFilters(newFilters);
    if (setCurrentPage) setCurrentPage(1);
    setShowFilterFor(null);
  }, [filters]);

  // Clear all filters
  const clearAllFilters = useCallback((setCurrentPage) => {
    setPendingFilters({});
    setFilters({});
    if (setCurrentPage) setCurrentPage(1);
    setShowFilterFor(null);
  }, []);

  const activeFiltersCount = useMemo(() => Object.keys(filters).length, [filters]);
  const hasActiveFilters = activeFiltersCount > 0;

  // Process filters for server: convert date fields from YYYY-MM-DD to ISO format
  const processFiltersForServer = useCallback((filtersToProcess) => {
    const processedFilters = { ...filtersToProcess };
    Object.keys(processedFilters).forEach(columnKey => {
      const filter = processedFilters[columnKey];
      if (filter && typeof filter === 'object') {
        // Check if this is a date filter (has fromdate or todate)
        if (filter.fromdate || filter.todate) {
          // Use helper function to convert date range
          const dateFilter = convertToDateRangeFilter(filter.fromdate, filter.todate);
          processedFilters[columnKey] = dateFilter;
        }
      }
    });
    return processedFilters;
  }, []);

  // Memoize return object to prevent unnecessary re-renders
  return useMemo(() => ({
    filters,
    setFilters,
    pendingFilters,
    setPendingFilters,
    showFilterFor,
    setShowFilterFor,
    filterGroups,
    setFilterGroups,
    maxValues,
    setMaxValues,
    handleFilterChange,
    handleMultiSelectChange,
    handleRangeChange,
    applyFilters,
    clearFilter,
    clearAllFilters,
    activeFiltersCount,
    hasActiveFilters,
    processFiltersForServer,
    getFilterOperator
  }), [
    filters,
    setFilters,
    pendingFilters,
    setPendingFilters,
    showFilterFor,
    setShowFilterFor,
    filterGroups,
    setFilterGroups,
    maxValues,
    setMaxValues,
    handleFilterChange,
    handleMultiSelectChange,
    handleRangeChange,
    applyFilters,
    clearFilter,
    clearAllFilters,
    activeFiltersCount,
    hasActiveFilters,
    processFiltersForServer,
    getFilterOperator
  ]);
}
